import { colorValue } from '@hai/ui-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Status } from 'src/js/component/actionBar/actionBar';
import { SortType } from 'src/js/component/sort-manager';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useStores } from 'src/js/hook/use-stores';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { useWatchObject } from 'src/js/store/use-watch';
import { PRODUCT, constant } from 'src/js/constant';
import { fetchEssences } from '../essence/essence-store';

import { StreamingActionBar } from 'src/js/component/actionBar/streaming-action-bar';
import { fetchVideoEncoders } from '../video-encoder/video-encoder-store';
import { AudioEncoderList } from './audio-encoder-list';
import { AudioEncoderViewModel, encoderIsMuted, encoderIsStopped, encoderModelToStatus } from './audio-encoder-models';
import encoders, { fetchAudioEncoders, muteUnmuteAudioEncoder, startStopAudioEncoder } from './audio-encoder-store';
import { useLocalStorage } from 'usehooks-ts';
import { fetchStreams } from '../stream/stream-store';

let connectionLost = false;
export const AudioEncodersPage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const [audioEncoders, setAudioEncoders] = useState<AudioEncoderViewModel[]>(encoders);
  useWatchObject(encoders, setAudioEncoders);
    const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();
  const scrollToId = location?.state?.id;

  const interval = constant.defaultRefreshInterval; 
  useAdaptiveFetch(
    () =>
      fetchAudioEncoders(t).then((res) => {
        setIsLoading(false);
        if (res.ok) {
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
      }),
    interval,
  );

  useAdaptiveFetch(
    () =>
      fetchStreams(t),
    interval,
  );

  const fetchData = async () => {
    if (systemInfoCapabilities.is2110()) {
      await fetchEssences(t);
    }
    if (constant.product === PRODUCT.FX) {
      fetchVideoEncoders(t); // for HDMI vs SDI
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  watchEvent('preset-load', () => {
    fetchAudioEncoders(t);
    fetchData();
  });

  const filteredStart = () => audioEncoders.filter((e) => e.selected && encoderIsStopped(e.state));
  const handleStartEncoder = () => {
    const filtered = filteredStart();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopAudioEncoder(e, true, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('audioEncoder.notifications.startErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredStop = () => audioEncoders.filter((e) => e.selected && !encoderIsStopped(e.state));
  const handleStopEncoder = () => {
    const filtered = filteredStop();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopAudioEncoder(e, false, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('audioEncoder.notifications.stopErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredMute = () =>
    audioEncoders.filter((e) => e.selected && !encoderIsMuted(e.state) && !encoderIsStopped(e.state));
  const handleMuteEncoder = () => {
    const filtered = filteredMute();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => muteUnmuteAudioEncoder(e, false, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in mute/unmute
          createAndDispatchNotification(
            t('audioEncoder.notifications.muteErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredUnmute = () =>
    audioEncoders.filter((e) => e.selected && encoderIsMuted(e.state) && !encoderIsStopped(e.state));
  const handleUnmuteEncoder = () => {
    const filtered = filteredUnmute();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => muteUnmuteAudioEncoder(e, true, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in mute/unmute
          createAndDispatchNotification(
            t('audioEncoder.notifications.unmuteErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const statusTypes: Status[] = [
    { status: 'ENCODING', color: colorValue('haiui-green-01') },
    { status: 'MUTED', color: colorValue('haiui-amber-01') },
    { status: 'INACTIVE', color: colorValue('haiui-gray-07') },
  ];

  const handleSelect = (item: AudioEncoderViewModel, selected: boolean) => {
    const encoders = audioEncoders.map((i: AudioEncoderViewModel) => (i.id === item.id ? { ...i, selected } : i));
    setAudioEncoders(encoders);
  };

  const onSelectAll = (selected: boolean) => {
    const models = audioEncoders.map((i) => ({ ...i, selected }));
    setAudioEncoders(models);
  };

  const [searchFilters, setSearchFilters] = useLocalStorage(
    `${constant.lStorage.searchFilter}:${sessionStore.username}:audenc`,
    [],
  );

  const sortInfo = {
    id: { title: t('sort.sortText.id'), type: SortType.GENERIC },
    name: { title: t('sort.sortText.name'), type: SortType.NATURAL_SORT },
    interfaceTxt: { title: t('sort.sortText.input'), type: SortType.NATURAL_SORT },
    modeTxt: { title: t('sort.sortText.channel'), type: SortType.NATURAL_SORT },
    langTxt: { title: t('sort.sortText.language'), type: SortType.NATURAL_SORT },
    bitRate: { title: t('sort.sortText.bitrate'), type: SortType.GENERIC },
    stateSortable: { title: t('sort.sortText.status'), type: SortType.GENERIC },
  };

  return (
    <StreamingActionBar
      items={audioEncoders}
      searchKeys={['name', 'interfaceTxt', 'langTxt', 'modeTxt', 'bitRate']}
      initialSearchFilters={searchFilters}
      isLoading={isLoading}
      onChangeSearchFilters={setSearchFilters}
      sortInfo={sortInfo}
      statusTypes={statusTypes}
      getStateValue={encoderModelToStatus}
      scrollToId={scrollToId}
      actionButtons={
        sessionStore.isUser()
          ? []
          : [
              { onClick: handleStartEncoder, counter: filteredStart, label: t('general.start') },
              { onClick: handleStopEncoder, counter: filteredStop, label: t('general.stop') },
              { onClick: handleMuteEncoder, counter: filteredMute, label: t('general.mute') },
              { onClick: handleUnmuteEncoder, counter: filteredUnmute, label: t('general.unmute') },
            ]
      }
      List={AudioEncoderList}
      onSelectAll={onSelectAll}
      updateSelection={handleSelect}
      storageKey="audenc"
      placeholderActions={4}
      placeholderNumPanels={constant.product === PRODUCT.FX ? 4 : 8}
    />
  );
};
